import React, { useContext } from 'react';

import {
  Collapsible, Badge, Box, Flex, Grid, Heading, Image, Text, Button,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import { midiToHelmholtz } from "../services/miditonotes";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { instrumentTexts } from "../i18n/instruments";
import { LanguageContext } from "../contexts/LanguageContext";
import { setPendingInstrument } from "../services/pendingReserve";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const SpecRow = ({ label, value }) => (
  <Flex justify="space-between" gap={4} py={1.5} borderBottomWidth="1px" borderColor="gray.100" _last={{ borderBottomWidth: 0 }}>
    <Text fontSize="sm" color="gray.500">{label}</Text>
    <Text fontSize="sm" color="gray.800" fontWeight="medium" textAlign="right">{value}</Text>
  </Flex>
);

const SectionLabel = ({ children }) => (
  <Text
    fontFamily="space"
    fontSize="xs"
    letterSpacing="0.2em"
    textTransform="uppercase"
    color="gray.500"
    mb={2}
  >
    {children}
  </Text>
);

// Model fields that are optional in the DB arrive as 0 / null / "nan"
const has = (v) => v !== null && v !== undefined && v !== 0 && v !== "" && v !== "nan";

const InstrumentDetails = ({ instrument, onBack, isLoggedIn }) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const t = instrumentTexts[language] || instrumentTexts["et"];
  const m = instrument.modelId;

  const imageUrl = useBreakpointValue({
    base: instrument.mobile_image,
    md: instrument.desktop_image,
  });

  const handleReserve = async () => {
    if (isLoggedIn) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/instruments/${instrument.instrumentId}/reserve`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',  // This is like withCredentials in axios
            body: JSON.stringify({ status: 'Reserved' }),
          }
        );

        if (response.ok) {
          // Navigate to rent page with instrument details
          navigate('/rent', { state: { instrument: { ...instrument, status: 'Reserved' } } });
        } else if (response.status === 403) {
          // Profile incomplete (address / email / phone) — finish it first
          navigate('/profile');
        } else if (response.status === 401) {
          navigate('/login');
        } else {
          console.error('Failed to reserve the instrument.');
        }
      } catch (error) {
        console.error('Error updating instrument status:', error);
      }
    } else {
      // Remember the selection so the rental flow resumes after login/register
      setPendingInstrument(instrument);
      navigate('/login');
    }
  };

  const info = language === "et" ? instrument.info_est : instrument.info_eng;

  return (
    <Box maxW="6xl" mx="auto">
      <Button
        onClick={onBack}
        variant="ghost"
        size="sm"
        fontFamily="space"
        fontSize="xs"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color="gray.600"
        _hover={{ color: "gray.900", bg: "gray.100" }}
        mb={5}
        px={2}
      >
        <ChevronLeft size={16} />
        {t.action.back}
      </Button>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={{ base: 6, md: 10 }}>
        {/* Image */}
        <Box
          bg="gray.100"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          overflow="hidden"
          alignSelf="start"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Image
            src={`${API_BASE_URL}${imageUrl}`}
            alt={`${m.brand} ${m.model}`}
            objectFit="contain"
            w="100%"
            maxH={{ base: "340px", md: "560px" }}
          />
        </Box>

        {/* Info */}
        <Box>
          <Text
            fontFamily="space"
            fontSize="xs"
            letterSpacing="0.25em"
            textTransform="uppercase"
            color="gray.500"
            mb={1}
          >
            {m.brand}
          </Text>
          <Heading
            as="h1"
            fontFamily="heading"
            fontWeight="500"
            fontSize={{ base: "4xl", md: "5xl" }}
            lineHeight="1.05"
            color="gray.900"
            mb={3}
          >
            {m.model}
          </Heading>

          <Flex align="center" gap={3} wrap="wrap" mb={2}>
            <Badge
              colorPalette={instrument.status === "Available" ? "green" : "orange"}
              variant="subtle"
              fontFamily="space"
              fontSize="10px"
              letterSpacing="0.15em"
              textTransform="uppercase"
              px={2}
              py={0.5}
              borderRadius="sm"
            >
              {t.status[instrument.status] || instrument.status}
            </Badge>
            <Box
              display="inline-flex"
              alignItems="center"
              px={2.5}
              py={0.5}
              bg="#09090b"
              color="white"
              borderRadius="sm"
              fontFamily="space"
              fontSize="10px"
              letterSpacing="0.15em"
              textTransform="uppercase"
            >
              {t.pricetext} {instrument.price_level}
            </Box>
          </Flex>

          {instrument.status !== "Available" && (
            <Text fontSize="sm" color="gray.600" mb={2}>
              {t.predictedAvailability(instrument.predicted_availability)}
            </Text>
          )}

          <Text fontSize="sm" color="gray.600">{t.pricetext1}</Text>
          <Text fontSize="sm" color="gray.600" mb={5}>{t.pricetext2}</Text>

          {instrument.status === 'Available' && (
            <Button
              onClick={handleReserve}
              size="lg"
              w={{ base: "full", md: "auto" }}
              px={10}
              bg="#09090b"
              color="white"
              borderRadius="sm"
              fontFamily="space"
              fontSize="xs"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
              mb={7}
            >
              {isLoggedIn ? t.action.reserve : t.action.loginToReserve}
            </Button>
          )}

          {/* Model data */}
          <SectionLabel>{t.headings.a}</SectionLabel>
          <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={5} py={3} mb={6}>
            <SpecRow
              label={t.color}
              value={t.colors[instrument.color] || instrument.color}
            />
            <SpecRow
              label={t.keys}
              value={`${m.keys} (${midiToHelmholtz(m.low)}–${midiToHelmholtz(m.low + m.keys - 1)})`}
            />
            <SpecRow label={t.basses} value={`${m.sb} (${m.bRows} ${language === "et" ? "rida" : "rows"})`} />
            {has(m.fb) && (
              <SpecRow label={t.labels.freebass} value={m.fb} />
            )}
            {has(m.reeds_fb) && (
              <SpecRow label={t.labels.freebassReeds} value={m.reeds_fb} />
            )}
            <SpecRow label={t.reeds} value={`${m.reedsR} / ${m.reedsL}`} />
            <SpecRow label={t.registers} value={`${m.regR} / ${m.regL}`} />
            {has(m.bass_start_notes) && (
              <SpecRow label={t.labels.bassStartNotes} value={m.bass_start_notes} />
            )}
            <SpecRow
              label={t.dimensions}
              value={`${m.height} × ${m.width}${has(m.length) ? ` × ${m.length}` : ""} cm`}
            />
            {has(m.deep) && (
              <SpecRow label={t.labels.depth} value={`${m.deep} cm`} />
            )}
            {has(m.folds) && (
              <SpecRow label={t.labels.folds} value={m.folds} />
            )}
            <SpecRow label={t.labels.weight} value={`${m.weight} kg`} />
            <SpecRow
              label={`${t.newprice} / ${t.usedprice}`}
              value={`${m.newPrice}€ / ${m.usedPrice}€`}
            />
          </Box>

          {/* Specific details (collapsible) */}
          <Collapsible.Root mb={6}>
            <Collapsible.Trigger
              display="flex"
              gap={2}
              alignItems="center"
              cursor="pointer"
              py={1}
            >
              <Collapsible.Indicator
                transition="transform 0.2s"
                _open={{ transform: "rotate(90deg)" }}
                color="gray.500"
              >
                <ChevronRight size={16} />
              </Collapsible.Indicator>
              <Text
                fontFamily="space"
                fontSize="xs"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color="gray.500"
              >
                {t.headings.b}
              </Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={5} py={3} mt={2}>
                <SpecRow label={t.labels.serial} value={instrument.serial} />
                {has(instrument.whitekeys) && (
                  <SpecRow label={t.labels.whitekeys} value={instrument.whitekeys} />
                )}
                {has(instrument.whitekeywidth) && (
                  <SpecRow
                    label={t.labels.whitekeywidth}
                    value={`${Math.round(instrument.whitekeywidth * 10) / 10} cm`}
                  />
                )}
                <SpecRow label={t.labels.keyboard} value={`${m.keyboard} cm`} />
                {has(instrument.keyboardmax) && (
                  <SpecRow
                    label={t.labels.keyboardmax}
                    value={`${Math.round(instrument.keyboardmax * 10) / 10} cm`}
                  />
                )}
              </Box>
            </Collapsible.Content>
          </Collapsible.Root>

          {/* Additional info */}
          {has(info) && (
            <>
              <SectionLabel>{t.headings.c}</SectionLabel>
              <Text fontSize="sm" color="gray.700" mb={6}>
                {info}
              </Text>
            </>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default InstrumentDetails;
